// WCAG 2.x contrast helpers (F-014).

export type Rgb = [number, number, number]

function channel(v: number): number {
  const s = v / 255
  return s <= 0.03928 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4
}

export function relativeLuminance([r, g, b]: Rgb): number {
  return 0.2126 * channel(r) + 0.7152 * channel(g) + 0.0722 * channel(b)
}

export function contrastRatio(a: Rgb, b: Rgb): number {
  const la = relativeLuminance(a)
  const lb = relativeLuminance(b)
  return (Math.max(la, lb) + 0.05) / (Math.min(la, lb) + 0.05)
}

const WHITE: Rgb = [255, 255, 255]

/** The WCAG AA floor for normal text, used for every brand pairing. */
export const BRAND_MIN_CONTRAST = 4.5

/** A colour's `/10` tint as it renders on white (bg-brand-orange/10). */
export function tintOnWhite(rgb: Rgb, alpha = 0.1): Rgb {
  return rgb.map((v) => Math.round(alpha * v + (1 - alpha) * 255)) as Rgb
}

/**
 * Darken a brand colour (keeping its hue) by the smallest step that makes
 * every pairing it is used in reach `minRatio`: white text on it (buttons),
 * the colour as text on white (links), and the colour as text on its own 10%
 * tint (active tabs, selected rows). A host that picks a light primary (the
 * teal #008491 reads 4.46:1 under white button text) still gets AA. Colours
 * that already pass come back unchanged.
 *
 * R1-F13: one rule for every brand. The default Basiyo orange in index.css is
 * this function applied to the original 220 107 47, so a host that sets its
 * branding to that orange gets the same colour as a host with no branding.
 */
export function ensureContrastOnWhite(rgb: Rgb, minRatio = BRAND_MIN_CONTRAST): Rgb {
  const passes = (c: Rgb) =>
    contrastRatio(c, WHITE) >= minRatio && contrastRatio(c, tintOnWhite(c)) >= minRatio
  let factor = 1
  let out: Rgb = rgb
  while (!passes(out) && factor > 0) {
    factor = Math.max(0, factor - 0.01)
    out = rgb.map((v) => Math.round(v * factor)) as Rgb
  }
  return out
}

const clampChannel = (v: number) => Math.max(0, Math.min(255, v))

/**
 * Review r2 N-7: the hover (dark) and light shades of a brand colour. One
 * rule for the default tokens in styles/index.css and for ThemeProvider's
 * host branding, so a host branded with the Basiyo orange gets the same
 * hover and focus shades as a host with no branding.
 */
export function brandShades([r, g, b]: Rgb): { light: Rgb; dark: Rgb } {
  return {
    light: [clampChannel(r + 25), clampChannel(g + 30), clampChannel(b + 30)],
    dark: [clampChannel(r - 25), clampChannel(g - 18), clampChannel(b - 15)],
  }
}
